import { useMemo, useRef } from "react";
import { ColDef, ColGroupDef } from "ag-grid-community";

import { SheetColumn, SheetRowData } from "../types";
import { GRID_DIMENSIONS } from "../constants";

interface UseTableColumnsProps<T extends SheetRowData> {
  columns: (SheetColumn<T> | ColGroupDef<T>)[];
  enableSorting: boolean;
  enableResizing: boolean;
  enableFiltering: boolean;
}

interface CachedTableColumn<T extends SheetRowData> {
  input: SheetColumn<T>;
  enableSorting: boolean;
  enableResizing: boolean;
  enableFiltering: boolean;
  output: ColDef<T>;
}

/**
 * Maps `SheetColumn<T>[]` into ag-grid `ColDef<T>[]`.
 *
 * Keeps the mapping pure – no side-effects, no store dependency.
 * Every ergonomic shorthand (`header`, `editableColumn`, `validationErrors`)
 * is resolved here so the rest of the table only deals with native ColDefs.
 */
export const useTableColumns = <T extends SheetRowData>({
  columns,
  enableSorting,
  enableResizing,
  enableFiltering,
}: UseTableColumnsProps<T>) => {
  const columnCacheRef = useRef<Map<string, CachedTableColumn<T>>>(new Map());

  const columnDefs = useMemo<(ColDef<T> | ColGroupDef<T>)[]>(() => {
    const isColumnGroupDef = (col: SheetColumn<T> | ColGroupDef<T>): col is ColGroupDef<T> =>
      Array.isArray((col as ColGroupDef<T>).children);

    const nextLeafIds = new Set<string>();

    const mapLeafColumn = (col: SheetColumn<T>): ColDef<T> => {
      const leafId = col.colId ?? col.field;
      if (leafId) {
        nextLeafIds.add(leafId);
        const cached = columnCacheRef.current.get(leafId);
        if (
          cached?.input === col &&
          cached.enableSorting === enableSorting &&
          cached.enableResizing === enableResizing &&
          cached.enableFiltering === enableFiltering
        ) {
          return cached.output;
        }
      }

      const { header, editableColumn, validationErrors, cellRendererType, ...nativeColDef } = col;
      const mapped: ColDef<T> = {
        ...nativeColDef,

        // header → headerName (fallback chain: header → headerName → field)
        headerName: header ?? nativeColDef.headerName ?? nativeColDef.field,

        // editableColumn → editable
        ...(editableColumn !== undefined && { editable: editableColumn }),

        // Inherit global toggles unless the column explicitly overrides
        sortable: nativeColDef.sortable ?? enableSorting,
        resizable: nativeColDef.resizable ?? enableResizing,
        filter: nativeColDef.filter ?? enableFiltering,

        // Sizing defaults – flex:1 when no explicit width constraint is set
        flex:
          nativeColDef.flex ??
          (nativeColDef.width || nativeColDef.minWidth || nativeColDef.maxWidth ? undefined : 1),
        minWidth: nativeColDef.minWidth ?? GRID_DIMENSIONS.defaultMinColumnWidth,
      };

      // Wire cellRendererType into cellRendererParams without clobbering existing params
      if (cellRendererType) {
        mapped.cellRendererParams = {
          ...(typeof nativeColDef.cellRendererParams === "object"
            ? nativeColDef.cellRendererParams
            : {}),
          cellRendererType,
        };
      }

      // Wire validation into cellEditorParams without clobbering existing params
      if (validationErrors) {
        mapped.cellEditorParams = {
          ...(typeof nativeColDef.cellEditorParams === "object"
            ? nativeColDef.cellEditorParams
            : {}),
          getValidationErrors: validationErrors,
        };
      }

      if (leafId) {
        columnCacheRef.current.set(leafId, {
          input: col,
          enableSorting,
          enableResizing,
          enableFiltering,
          output: mapped,
        });
      }

      return mapped;
    };

    const mapColumn = (col: SheetColumn<T> | ColGroupDef<T>): ColDef<T> | ColGroupDef<T> => {
      if (!isColumnGroupDef(col)) {
        return mapLeafColumn(col);
      }

      return {
        ...col,
        children: col.children?.map(child => mapColumn(child as SheetColumn<T> | ColGroupDef<T>)),
      };
    };

    const mappedColumns = columns.map(col => mapColumn(col));

    for (const leafId of columnCacheRef.current.keys()) {
      if (!nextLeafIds.has(leafId)) {
        columnCacheRef.current.delete(leafId);
      }
    }

    return mappedColumns;
  }, [columns, enableSorting, enableResizing, enableFiltering]);

  return { columnDefs };
};
